import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SettingsSurface, SettingToggle, PreviewStage } from "@/features/settings/components/SettingsSurface";
import type { DocumentThemeV1 } from "../pages/CompanyPage";

interface Props {
	mode: "document" | "receipt";
	theme: DocumentThemeV1;
	setTheme: React.Dispatch<React.SetStateAction<DocumentThemeV1>>;
	disabled: boolean;
	onReset: () => void;
	name?: string;
	address?: string;
	taxNumber?: string;
	logo?: string;
	currency: string;
	receiptHeader: string;
	receiptFooter: string;
	showLogo: boolean;
	showTax: boolean;
	showDiscount: boolean;
	setReceiptHeader: (v: string) => void;
	setReceiptFooter: (v: string) => void;
	setShowLogo: (v: boolean) => void;
	setShowTax: (v: boolean) => void;
	setShowDiscount: (v: boolean) => void;
}

export default function PrintSettings(p: Props) {
	const { t } = useTranslation();
	const theme = p.theme;
	const primary = /^#[\da-f]{6}$/i.test(theme.primaryColor) ? theme.primaryColor : "#1c4585";
	const accent = /^#[\da-f]{6}$/i.test(theme.accentColor) ? theme.accentColor : "#0ea5e9";
	const rows = [
		[t("settingsDesign.sampleCoffee"), "2", "18.00", "36.00"],
		[t("settingsDesign.sampleBread"), "2", "12.00", "24.00"],
		[t("settingsDesign.sampleTea"), "1", "40.00", "40.00"],
	];
	const totals = (tax: boolean, discount = false) => (
		<>
			<div className="print-total-row">
				<span>{t("settingsDesign.subtotal")}</span>
				<bdi>100.00</bdi>
			</div>
			{discount && (
				<div className="print-total-row">
					<span>{t("workspace.discount")}</span>
					<bdi>0.00</bdi>
				</div>
			)}
			{tax && (
				<div className="print-total-row">
					<span>{t("workspace.tax")} (15%)</span>
					<bdi>15.00</bdi>
				</div>
			)}
			<div className="print-total-row print-grand-total">
				<strong>{t("workspace.total")}</strong>
				<strong>
					<bdi>115.00 {p.currency}</bdi>
				</strong>
			</div>
		</>
	);
	return (
		<>
			{p.mode === "document" && (
				<div className="settings-editor-grid">
					<SettingsSurface className="space-y-6">
						<div>
							<h2 className="text-base font-semibold">{t("company.documents.title")}</h2>
							<p className="mt-1 text-sm text-muted-foreground">{t("company.documents.description")}</p>
						</div>
						<fieldset className="space-y-3">
							<legend className="mb-3 text-sm font-medium">{t("company.documents.preset")}</legend>
							<div className="grid grid-cols-3 gap-2">
								{(["modern", "minimal", "bold"] as const).map(preset => (
									<button
										key={preset}
										type="button"
										className="settings-preset"
										aria-pressed={theme.preset === preset}
										disabled={p.disabled}
										onClick={() => p.setTheme(v => ({ ...v, preset }))}
									>
										<span aria-hidden="true" className={`preset-paper preset-${preset}`}>
											<span />
											<i />
											<i />
											<i />
											<b />
										</span>
										<span>
											{t(`company.documents.preset${preset[0].toUpperCase()}${preset.slice(1)}`)}
										</span>
									</button>
								))}
							</div>
						</fieldset>
						<div className="grid gap-4 sm:grid-cols-2">
							{(
								[
									["primaryColor", "docPrimary"],
									["accentColor", "docAccent"],
								] as const
							).map(([key, id]) => (
								<div key={key} className="space-y-2">
									<Label htmlFor={id}>{t(`company.documents.${key}`)}</Label>
									<div className="flex gap-2">
										<Input
											id={id}
											type="color"
											value={theme[key]}
											disabled={p.disabled}
											className="w-12 shrink-0 cursor-pointer p-1"
											onChange={e => p.setTheme(v => ({ ...v, [key]: e.target.value }))}
										/>
										<Input
											aria-label={t(`company.documents.${key}`)}
											dir="ltr"
											className="min-w-0 font-mono"
											value={theme[key]}
											disabled={p.disabled}
											onChange={e => p.setTheme(v => ({ ...v, [key]: e.target.value }))}
										/>
									</div>
								</div>
							))}
							<div className="space-y-2">
								<Label>{t("company.documents.headerLayout")}</Label>
								<Select
									value={theme.headerLayout}
									disabled={p.disabled}
									onValueChange={v =>
										p.setTheme(prev => ({
											...prev,
											headerLayout: v as DocumentThemeV1["headerLayout"],
										}))
									}
								>
									<SelectTrigger aria-label={t("company.documents.headerLayout")}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="logoLeft">
											{t("company.documents.layoutLogoLeft")}
										</SelectItem>
										<SelectItem value="logoCenter">
											{t("company.documents.layoutLogoCenter")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>{t("company.documents.tableDensity")}</Label>
								<Select
									value={theme.tableDensity}
									disabled={p.disabled}
									onValueChange={v =>
										p.setTheme(prev => ({
											...prev,
											tableDensity: v as DocumentThemeV1["tableDensity"],
										}))
									}
								>
									<SelectTrigger aria-label={t("company.documents.tableDensity")}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="comfortable">
											{t("company.documents.densityComfortable")}
										</SelectItem>
										<SelectItem value="compact">{t("company.documents.densityCompact")}</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<div>
							<SettingToggle
								id="docShowLogo"
								label={t("company.documents.showLogo")}
								checked={theme.showLogo}
								disabled={p.disabled}
								onChange={v => p.setTheme(prev => ({ ...prev, showLogo: v }))}
							/>
							<SettingToggle
								id="docShowTax"
								label={t("company.documents.showTaxRow")}
								checked={theme.showTaxRow}
								disabled={p.disabled}
								onChange={v => p.setTheme(prev => ({ ...prev, showTaxRow: v }))}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="docFooter">{t("company.documents.footerNote")}</Label>
							<Textarea
								id="docFooter"
								rows={3}
								value={theme.footerNote}
								disabled={p.disabled}
								placeholder={t("company.documents.footerNotePlaceholder")}
								onChange={e => p.setTheme(v => ({ ...v, footerNote: e.target.value }))}
							/>
						</div>
						{!p.disabled && (
							<Button variant="outline" onClick={p.onReset}>
								{t("company.documents.resetTheme")}
							</Button>
						)}
					</SettingsSurface>
					<PreviewStage
						title={t("workspace.documentPreview")}
						description={t("settingsDesign.documentSample")}
					>
						<div
							data-testid="document-preview"
							className={`print-paper document-paper paper-${theme.preset}`}
							style={{ "--paper-primary": primary, "--paper-accent": accent } as React.CSSProperties}
						>
							<header
								className={`paper-heading ${theme.headerLayout === "logoCenter" ? "paper-heading-centered" : ""}`}
							>
								{theme.showLogo && p.logo && <img src={p.logo} alt="" className="paper-logo" />}
								<div className="min-w-0">
									<h3>{p.name || t("settingsDesign.storeName")}</h3>
									<p>{p.address}</p>
									{p.taxNumber && (
										<p>
											{t("company.fields.taxNumber")}: <bdi>{p.taxNumber}</bdi>
										</p>
									)}
								</div>
							</header>
							<div className="paper-invoice-heading">
								<strong>{t("settingsDesign.invoice")}</strong>
								<bdi>INV-0001</bdi>
							</div>
							<div className="paper-meta">
								<div>
									<span>{t("settingsDesign.billedTo")}</span>
									<strong>{t("settingsDesign.sampleCustomer")}</strong>
								</div>
								<div>
									<span>{t("common.date")}</span>
									<bdi>27/10/2026</bdi>
								</div>
							</div>
							<div
								className={`paper-lines ${theme.tableDensity === "compact" ? "paper-lines-compact" : ""}`}
							>
								<div className="paper-line paper-line-head">
									<span>{t("settingsDesign.item")}</span>
									<span>{t("settingsDesign.quantity")}</span>
									<span>{t("settingsDesign.price")}</span>
									<span>{t("workspace.total")}</span>
								</div>
								{rows.map(row => (
									<div className="paper-line" key={row[0]}>
										{row.map((v, i) => (
											<span key={i}>{i ? <bdi>{v}</bdi> : v}</span>
										))}
									</div>
								))}
							</div>
							<div className="paper-totals">{totals(theme.showTaxRow)}</div>
							<footer className="paper-footer">{theme.footerNote}</footer>
						</div>
					</PreviewStage>
				</div>
			)}
			{p.mode === "receipt" && (
				<div className="settings-editor-grid">
					<SettingsSurface className="space-y-6">
						<div>
							<h2 className="text-base font-semibold">{t("settings.receipts.title")}</h2>
							<p className="mt-1 text-sm text-muted-foreground">{t("settingsDesign.receiptHint")}</p>
						</div>
						<div className="space-y-2">
							<Label htmlFor="receiptHeader">{t("settings.receipts.receiptHeader")}</Label>
							<Input
								id="receiptHeader"
								value={p.receiptHeader}
								readOnly={p.disabled}
								placeholder={t("settings.receipts.receiptHeaderPlaceholder")}
								onChange={e => p.setReceiptHeader(e.target.value)}
							/>
						</div>
						<div>
							<SettingToggle
								id="showLogo"
								label={t("settings.receipts.showCompanyLogo")}
								checked={p.showLogo}
								disabled={p.disabled}
								onChange={p.setShowLogo}
							/>
							<SettingToggle
								id="showTax"
								label={t("settings.receipts.showTaxDetails")}
								checked={p.showTax}
								disabled={p.disabled}
								onChange={p.setShowTax}
							/>
							<SettingToggle
								id="showDiscount"
								label={t("settings.receipts.showDiscountDetails")}
								checked={p.showDiscount}
								disabled={p.disabled}
								onChange={p.setShowDiscount}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="receiptFooter">{t("settings.receipts.receiptFooter")}</Label>
							<Textarea
								id="receiptFooter"
								rows={3}
								value={p.receiptFooter}
								readOnly={p.disabled}
								placeholder={t("settings.receipts.receiptFooterPlaceholder")}
								onChange={e => p.setReceiptFooter(e.target.value)}
							/>
						</div>
					</SettingsSurface>
					<PreviewStage title={t("workspace.receiptPreview")} description={t("workspace.sampleReceipt")}>
						<div data-testid="receipt-preview" className="print-paper receipt-paper">
							<header className="text-center">
								{p.showLogo && p.logo && (
									<img src={p.logo} alt="" className="paper-logo mx-auto grayscale" />
								)}
								<h3>{p.name || t("settingsDesign.storeName")}</h3>
								<p>{p.address}</p>
								{p.taxNumber && (
									<p>
										{t("company.fields.taxNumber")}: <bdi>{p.taxNumber}</bdi>
									</p>
								)}
								<p className="whitespace-pre-wrap">{p.receiptHeader}</p>
							</header>
							<h4 className="my-4 text-center font-bold">{t("settingsDesign.receiptTab")}</h4>
							<div className="receipt-meta">
								<bdi>INV-0001</bdi>
								<bdi>27/10/2026 · 14:30</bdi>
							</div>
							<div className="receipt-lines">
								{rows.map(row => (
									<div key={row[0]}>
										<span>
											{row[1]} × {row[0]}
										</span>
										<bdi>{row[3]}</bdi>
									</div>
								))}
							</div>
							<div className="receipt-totals">{totals(p.showTax, p.showDiscount)}</div>
							<footer className="paper-footer">{p.receiptFooter}</footer>
						</div>
					</PreviewStage>
				</div>
			)}
		</>
	);
}
